<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <div class="principal-detail-shell sba-slide-in">
      <div class="card-panel principal-overview-card z-depth-0">
        <div class="principal-overview-head">
          <div class="principal-title-block">
            <span class="principal-status-icon" :class="{ danger: report.utilization_rate >= 50 }">
              <i class="material-icons">{{ report.utilization_rate < 50 ? "person" : "flag" }}</i>
            </span>
            <div>
              <label class="active teal-text">Individual Report</label>
              <h5>{{ report.principal.name }}</h5>
              <p>
                <span class="chip grey lighten-4">{{ report.principal.enrollee_number || "No ID" }}</span>
                <span class="chip teal lighten-5">{{ report.policy.year }}</span>
                <small class="grey-text">{{ policyDateRange(report.policy) }}</small>
              </p>
            </div>
          </div>
          <button class="btn-flat close-detail-button" title="Close" @click.prevent="handleBack">
            <i class="material-icons left">close</i>Close
          </button>
        </div>

        <div class="principal-overview-body">
          <div class="overview-metrics">
            <div class="overview-metric total">
              <h6>{{ formatMoney(report.invoice.generated) }}</h6>
              <small>Total Invoices</small>
            </div>
            <div class="overview-metric paid">
              <h6>{{ formatMoney(report.invoice.paid) }}</h6>
              <small>Premium Received</small>
            </div>
            <div class="overview-metric encounters">
              <h6>{{ report.encounter.count }}</h6>
              <small>Encounters</small>
            </div>
            <div class="overview-metric mlr">
              <h6 :class="mlrClass(report.gross_mlr ?? report.utilization_rate)">{{ report.gross_mlr ?? report.utilization_rate }}%</h6>
              <small>Gross MLR | <b>{{ formatMoney(report.encounter.gross_sent ?? report.encounter.sent) }}</b> billed</small>
              <small class="real-mlr-line">Real MLR {{ report.real_mlr ?? 0 }}% | {{ formatMoney(report.encounter.real_approved) }} vetted</small>
            </div>
          </div>
          <div class="progress">
            <div
              class="determinate"
              :class="barClass(report.utilization_rate)"
              :style="`width: ${barWidth(report.utilization_rate)}%`"
            ></div>
          </div>
          <div class="invoice-stack" :title="`Premium received ${formatMoney(report.invoice.paid)} | Pending invoices ${formatMoney(report.invoice.pending)}`">
            <span class="paid" :style="`width: ${invoiceWidth(report.invoice.paid, report.invoice.generated)}%`"></span>
            <span class="pending" :style="`width: ${invoiceWidth(report.invoice.pending, report.invoice.generated)}%`"></span>
          </div>
          <small class="grey-text">
            <span class="legend-chip"><i class="legend-box paid"></i>Premium Received</span>
            <span class="legend-chip"><i class="legend-box pending"></i>Pending Invoices</span>
          </small>
          <button class="btn-small waves-effect waves-light cyan darken-3" @click.prevent="downloadPrincipal">
            <i class="material-icons left">download</i>Download
          </button>
        </div>
      </div>

    <div class="card-panel family-panel">
          <h6>Family / Enrollees</h6>
          <ul class="collection">
            <li class="collection-item" v-for="person in report.family" :key="person.id">
              <strong>{{ person.name }}</strong><br />
              <small>{{ labelType(person.type) }} | {{ person.enrollee_number }}</small>
            </li>
          </ul>
          <p class="grey-text" v-if="!report.family.length">No family enrollees found for this policy.</p>
        </div>

        <div class="card-panel monthly-panel">
          <h6>Monthly Utilization</h6>
          <div class="month-row" v-for="month in report.monthly_utilization" :key="month.month">
            <span>{{ month.month }}</span>
            <div class="month-bar">
              <span :style="`width: ${monthlyWidth(month.sent)}%`"></span>
            </div>
            <strong>{{ formatMoney(month.sent) }}</strong>
          </div>
          <p class="grey-text" v-if="!report.monthly_utilization.length">No encounters for this policy.</p>
        </div>

        <div class="card-panel">
          <h6>Invoice Periods</h6>
          <div class="period-row" v-for="period in report.invoice_periods" :key="period.id">
            <div>
              <strong>{{ formatDate(period.start) }} - {{ formatDate(period.end) }}</strong><br />
              <small>
                {{ period.status.toUpperCase() }} |
                {{ formatMoney(period.invoice.generated) }} total invoice |
                {{ formatMoney(period.invoice.paid) }} premium received |
                {{ period.encounter.count }} encounters
              </small>
            </div>
            <div class="right-align">
              <strong :class="periodMlrClass(period)">{{ periodMlrLabel(period) }}</strong><br />
              <small>{{ formatMoney(period.encounter.gross_sent ?? period.encounter.sent) }} gross utilization</small><br />
              <small>{{ formatMoney(period.encounter.real_approved) }} real utilization</small>
            </div>
            <div class="progress period-progress">
              <div
                class="determinate"
                :class="barClass(period.utilization_rate)"
                :style="`width: ${barWidth(period.utilization_rate)}%`"
              ></div>
            </div>
            <div class="invoice-stack period-stack">
              <span class="paid" :style="`width: ${invoiceWidth(period.invoice.paid, period.invoice.generated)}%`"></span>
              <span class="pending" :style="`width: ${invoiceWidth(period.invoice.pending, period.invoice.generated)}%`"></span>
            </div>
          </div>
          <div class="period-total-row">
            <div>
              <strong>Principal / family total</strong><br />
              <small>{{ formatMoney(report.invoice.paid) }} premium received | {{ formatMoney(report.invoice.pending) }} pending invoices</small>
            </div>
            <div class="right-align">
              <strong :class="mlrClass(report.gross_mlr ?? report.utilization_rate)">{{ report.gross_mlr ?? report.utilization_rate }}% Gross MLR</strong><br />
              <small>{{ report.real_mlr ?? 0 }}% Real MLR | {{ formatMoney(report.encounter.real_approved) }} vetted</small>
            </div>
          </div>
          <p class="grey-text period-note">
            Principal MLR uses total utilization divided by premium received. Invoice-period MLR is not additive.
          </p>
          <p class="grey-text" v-if="!report.invoice_periods.length">No invoice periods found.</p>
        </div>

        <div class="card-panel">
          <h6>Provider Utilization</h6>
          <div class="provider-row" v-for="provider in report.provider_utilization" :key="provider.provider_id">
            <div>
              <strong>{{ provider.provider?.name || "Provider" }}</strong><br />
              <small>{{ provider.encounter_count }} encounters</small>
            </div>
            <div class="right-align">
              <strong>{{ formatMoney(provider.gross_sent ?? provider.sent) }}</strong><br />
              <small>{{ formatMoney(provider.real_approved) }} real</small>
            </div>
          </div>
          <p class="grey-text" v-if="!report.provider_utilization.length">No provider utilization found.</p>
        </div>

    <div class="card-panel">
      <h6>Encounters</h6>
      <div class="encounter-filter-grid">
        <div>
          <div class="input-field encounter-search">
            <label class="active">Search</label>
            <input
              type="search"
              v-model="encounterSearch"
              placeholder="Search enrollee, provider, diagnosis or status"
              @keyup.enter="handleEncounterSearch"
              @search="handleEncounterSearch"
            />
          </div>
        </div>
        <div>
          <div class="input-field">
            <select v-model="encounterProvider" aria-label="Provider" @change="handleEncounterSearch">
              <option value="">All providers</option>
              <option :value="provider.provider_id" v-for="provider in report.provider_utilization" :key="provider.provider_id">
                {{ provider.provider?.name || "Provider" }}
              </option>
            </select>
          </div>
        </div>
        <div>
          <div class="input-field">
            <select v-model="encounterMonth" aria-label="Month" @change="handleEncounterSearch">
              <option value="">All months</option>
              <option :value="month.month" v-for="month in report.monthly_utilization" :key="month.month">
                {{ month.month }}
              </option>
            </select>
          </div>
        </div>
        <div class="encounter-actions">
          <button class="btn-small waves-effect waves-light grey" :class="{ disabled: !encounterSearch && !encounterProvider && !encounterMonth }" @click.prevent="clearEncounterFilters">
            <i class="material-icons left">clear</i>Clear
          </button>
        </div>
      </div>
      <div class="collection">
        <div class="collection-item" v-for="encounter in pagedEncounters" :key="encounter.id">
          <div class="row m-0">
            <div class="col s12 m7">
              <strong>{{ encounter.name }}</strong><br />
              <small>{{ encounter.provider?.name || "Provider" }} | {{ encounter.diagnosis || "No diagnosis" }}</small>
            </div>
            <div class="col s12 m5 right-align">
                <strong>{{ formatMoney(encounter.gross_sent ?? encounter.sent) }}</strong><br />
              <small>{{ formatDate(encounter.encounter_date) }}</small>
            </div>
          </div>
        </div>
      </div>
      <div class="encounter-pager" v-if="encounterPageTotal > 1">
        <button
          class="btn-flat encounter-page-button"
          :class="{ disabled: encounterPage <= 1 }"
          @click.prevent="changeEncounterPage(encounterPage - 1)"
        >
          <i class="material-icons left">chevron_left</i>Previous
        </button>
        <span>Page {{ encounterPage }} of {{ encounterPageTotal }}</span>
        <button
          class="btn-flat encounter-page-button"
          :class="{ disabled: encounterPage >= encounterPageTotal }"
          @click.prevent="changeEncounterPage(encounterPage + 1)"
        >
          Next<i class="material-icons right">chevron_right</i>
        </button>
      </div>
      <p class="grey-text" v-if="!pagedEncounters.length">No encounters match this filter.</p>
    </div>
    </div>
  </template>
</template>

<script>
import { onMounted, onUpdated, ref, computed } from "vue";
import getItems from "@/composables/getItems";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { downloadExcelFx } from "@/composables/reportFunction/downloader";
import nativeUi from "@/ui/native-ui";

export default {
  name: "PrincipalDetailInsightComponent",
  components: { LoadingDiv },
  props: ["clientId", "principalId", "policyId", "encounterDate"],
  emits: ["reset"],
  setup(props, context) {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const encounterSearch = ref("");
    const encounterProvider = ref("");
    const encounterMonth = ref("");
    const encounterPage = ref(1);
    const encounterPageSize = 10;

    const initSelect = () => {
      const elemsSelect = document.querySelectorAll("select");
      nativeUi.FormSelect.init(elemsSelect, {});
    };

    onMounted(initSelect);
    onUpdated(initSelect);

    const formatMoney = (val) => {
      return `NGN ${Number(val || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    };

    const formatDate = (val) => {
      if (!val) return "";
      return new Date(val).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const policyDateRange = (policy) => {
      if (!policy?.start_date || !policy?.end_date) return "";
      return `${formatDate(policy.start_date)} - ${formatDate(policy.end_date)}`;
    };

    const labelType = (type) => {
      if (type === "p") return "Principal";
      if (type === "s") return "Spouse";
      if (type === "d") return "Dependant";
      return "Enrollee";
    };

    const barWidth = (val) => Math.min(Number(val || 0), 100);

    const barClass = (val) => {
      if (val >= 100) return "red darken-1";
      if (val > 70) return "orange darken-1";
      return "green darken-1";
    };

    const mlrClass = (val) => {
      if (val >= 100) return "mlr-red";
      if (val > 70) return "mlr-orange";
      return "mlr-green";
    };

    const periodMlrLabel = (period) => {
      if (Number(period?.invoice?.paid || 0) <= 0 && Number(period?.encounter?.sent || 0) > 0) {
        return "N/A Gross MLR";
      }

      return `${period?.gross_mlr ?? period?.utilization_rate ?? 0}% Gross MLR`;
    };

    const periodMlrClass = (period) => {
      if (Number(period?.invoice?.paid || 0) <= 0 && Number(period?.encounter?.sent || 0) > 0) {
        return "mlr-muted";
      }

      return mlrClass(period?.gross_mlr ?? period?.utilization_rate ?? 0);
    };

    const invoiceWidth = (val, total) => {
      if (!Number(total || 0)) {
        return 0;
      }

      return Math.min(Math.round((Number(val || 0) / Number(total || 0)) * 100), 100);
    };

    const monthlyWidth = (sent) => {
      const max = Math.max(...report.value.monthly_utilization.map((x) => Number(x.sent || 0)), 0);
      if (!max) return 0;
      return Math.round((Number(sent || 0) / max) * 100);
    };

    const buildPrincipalInsightUrl = (page = encounterPage.value) => {
      const params = [];

      if (props.policyId) {
        params.push(`policy_id=${props.policyId}`);
      }

      if (!props.policyId && props.encounterDate) {
        params.push(`encounter_date=${encodeURIComponent(props.encounterDate)}`);
      }

      params.push(`encounter_page=${page}`);
      params.push(`encounter_per_page=${encounterPageSize}`);

      if (encounterSearch.value) {
        params.push(`encounter_q=${encodeURIComponent(encounterSearch.value)}`);
      }

      if (encounterProvider.value) {
        params.push(`encounter_provider=${encounterProvider.value}`);
      }

      if (encounterMonth.value) {
        params.push(`encounter_month=${encounterMonth.value}`);
      }

      return `operator/insight/utilization/client/${props.clientId}/principal/${props.principalId}?${params.join("&")}`;
    };

    const getItemsFromApi = (page = encounterPage.value) => {
      (async () => {
        const url = buildPrincipalInsightUrl(page);
        const { res, err } = await getItems(url);

        if (res.value) {
          response.value = res.value.data;
          encounterPage.value = res.value.data.encounters?.current_page || 1;
        } else {
          error.value = err.value;
        }

        isReady.value = true;
      })();
    };

    onMounted(() => {
      getItemsFromApi();
    });

    const handleBack = () => {
      context.emit("reset", true);
    };

    const handleEncounterSearch = () => {
      encounterPage.value = 1;
      getItemsFromApi(1);
    };

    const changeEncounterPage = (page) => {
      if (page < 1 || page > encounterPageTotal.value) return;
      encounterPage.value = page;
      getItemsFromApi(page);
    };

    const downloadPrincipal = () => {
      const data = [
        {
          Section: "Summary",
          Name: report.value.principal.name,
          Detail: report.value.policy.year,
          "Policy Start Date": report.value.policy.start_date || "",
          "Policy End Date": report.value.policy.end_date || "",
          Provider: "",
          Date: "",
          Diagnosis: "",
          "Total Invoices": report.value.invoice.generated,
          "Premium Received": report.value.invoice.paid,
          "Pending Invoices": report.value.invoice.pending,
          Encounters: report.value.encounter.count,
          "Gross Utilization": report.value.encounter.gross_sent ?? report.value.encounter.sent,
          "Real Utilization": report.value.encounter.real_approved,
          "Gross MLR": `${report.value.gross_mlr ?? report.value.utilization_rate}%`,
          "Real MLR": `${report.value.real_mlr ?? 0}%`,
        },
        {},
      ];

      report.value.family.forEach((person, index) => {
        data.push({
          Section: index === 0 ? "Family / Enrollees" : "",
          Name: person.name,
          Detail: `${labelType(person.type)} ${person.enrollee_number || ""}`.trim(),
          Provider: "",
          Date: "",
          Diagnosis: "",
          "Total Invoices": "",
          "Premium Received": "",
          "Pending Invoices": "",
          Encounters: "",
          "Gross Utilization": "",
          "Real Utilization": "",
          MLR: "",
        });
      });

      data.push({});

      report.value.invoice_periods.forEach((period, index) => {
        data.push({
          Section: index === 0 ? "Invoice Periods" : "",
          Name: `${period.start} - ${period.end}`,
          Detail: "",
          Provider: "",
          Date: "",
          Diagnosis: "",
          "Total Invoices": period.invoice.generated,
          "Premium Received": period.invoice.paid,
          "Pending Invoices": period.invoice.pending,
          Encounters: period.encounter.count,
          "Gross Utilization": period.encounter.gross_sent ?? period.encounter.sent,
          "Real Utilization": period.encounter.real_approved,
          "Gross MLR": `${period.gross_mlr ?? period.utilization_rate}%`,
          "Real MLR": `${period.real_mlr ?? 0}%`,
        });
      });

      data.push({});

      report.value.monthly_utilization.forEach((month, index) => {
        data.push({
          Section: index === 0 ? "Monthly Utilization" : "",
          Name: month.month,
          Detail: "",
          Provider: "",
          Date: "",
          Diagnosis: "",
          "Total Invoices": "",
          "Premium Received": "",
          "Pending Invoices": "",
          Encounters: month.encounter_count,
          "Gross Utilization": month.gross_sent ?? month.sent,
          "Real Utilization": month.real_approved,
          MLR: "",
        });
      });

      data.push({});

      report.value.provider_utilization.forEach((provider, index) => {
        data.push({
          Section: index === 0 ? "Provider Utilization" : "",
          Name: provider.provider?.name || "Provider",
          Detail: "",
          Provider: "",
          Date: "",
          Diagnosis: "",
          "Total Invoices": "",
          "Premium Received": "",
          "Pending Invoices": "",
          Encounters: provider.encounter_count,
          "Gross Utilization": provider.gross_sent ?? provider.sent,
          "Real Utilization": provider.real_approved,
          MLR: "",
        });
      });

      data.push({});

      pagedEncounters.value.forEach((encounter, index) => {
        data.push({
          Section: index === 0 ? "Encounters" : "",
          Name: encounter.name,
          Detail: "",
          Provider: encounter.provider?.name || "",
          Date: encounter.encounter_date,
          Diagnosis: encounter.diagnosis || "",
          "Total Invoices": "",
          "Premium Received": "",
          "Pending Invoices": "",
          Encounters: "",
          "Gross Utilization": encounter.gross_sent ?? encounter.sent,
          "Real Utilization": encounter.real_approved,
          MLR: "",
        });
      });

      if (data.length) {
        downloadExcelFx(data, `${report.value.principal.name}_Utilization_${report.value.policy.year}`);
      }
    };

    const clearEncounterFilters = () => {
      encounterSearch.value = "";
      encounterProvider.value = "";
      encounterMonth.value = "";
      encounterPage.value = 1;
      getItemsFromApi(1);
    };

    const report = computed(() => response.value || {
      client: {},
      policy: {},
      principal: {},
      family: [],
      invoice: {},
      encounter: {
        sent: 0,
        gross_sent: 0,
        real_approved: 0,
      },
      invoice_periods: [],
      monthly_utilization: [],
      provider_utilization: [],
      encounters: {
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: encounterPageSize,
        total: 0,
      },
      utilization_rate: 0,
      gross_mlr: 0,
      real_mlr: 0,
    });

    const pagedEncounters = computed(() => {
      return report.value.encounters?.data || [];
    });

    const encounterPageTotal = computed(() => {
      return report.value.encounters?.last_page || 1;
    });

    return {
      handleBack,
      handleEncounterSearch,
      changeEncounterPage,
      downloadPrincipal,
      clearEncounterFilters,
      formatMoney,
      formatDate,
      policyDateRange,
      labelType,
      barWidth,
      barClass,
      mlrClass,
      periodMlrLabel,
      periodMlrClass,
      invoiceWidth,
      monthlyWidth,
      isReady,
      error,
      encounterSearch,
      encounterProvider,
      encounterMonth,
      encounterPage,
      encounterPageTotal,
      report,
      pagedEncounters,
    };
  },
};
</script>

<style scoped>
.clickable {
  cursor: pointer;
}

.m-0 {
  margin: 0;
}

.principal-detail-shell {
  display: grid;
  gap: 16px;
  max-height: calc(100vh - 84px);
  overflow-y: auto;
  padding-right: 4px;
}

.principal-detail-shell > .card-panel {
  margin: 0;
  width: 100%;
}

.card-panel {
  animation: insightCardIn 0.24s ease both;
}

.card-panel {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: none;
  color: #263238;
  margin: 0;
  padding: 24px 28px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.card-panel:hover {
  border-color: #cfd8dc;
  box-shadow: 0 8px 22px rgba(38, 50, 56, 0.12);
  transform: translateY(-1px);
}

.principal-overview-card {
  padding: 0;
}

.principal-overview-head {
  align-items: center;
  border-bottom: 1px solid #e7e7e7;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  min-height: 126px;
  padding: 26px 28px;
}

.principal-title-block {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  min-width: 0;
}

.principal-status-icon {
  align-items: center;
  background: #e0f2f1;
  border-radius: 999px;
  color: #00838f;
  display: flex;
  flex-shrink: 0;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.principal-status-icon.danger {
  background: #ffebee;
  color: #c62828;
}

.principal-status-icon i {
  font-size: 24px;
}

.principal-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
  overflow-wrap: anywhere;
}

.principal-title-block p {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 0;
}

.principal-title-block .chip {
  margin: 0;
}

.principal-title-block small {
  display: inline-block;
  margin-left: 2px;
}

.close-detail-button {
  color: #455a64;
  flex-shrink: 0;
  font-weight: 600;
  text-transform: none;
}

.principal-overview-body {
  padding: 24px 28px;
}

.overview-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 10px;
}

.overview-metric {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  min-height: 96px;
  padding: 16px;
}

.overview-metric h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 700;
  margin: 0 0 7px;
  overflow-wrap: anywhere;
}

.overview-metric small {
  color: #607d8b;
}

.overview-metric .real-mlr-line {
  display: block;
  font-size: 0.78rem;
  margin-top: 5px;
}

.overview-metric.total {
  background: #e3f2fd;
  border-color: #bbdefb;
}

.overview-metric.paid {
  background: #e0f2f1;
  border-color: #b2dfdb;
}

.overview-metric.encounters {
  background: #fff8e1;
  border-color: #ffecb3;
}

.overview-metric.mlr {
  background: #ffebee;
  border-color: #ffcdd2;
}

.mlr-green {
  color: #2e7d32;
}

.mlr-orange {
  color: #ef6c00;
}

.mlr-red {
  color: #c62828;
}

.mlr-muted {
  color: #78909c;
}

.month-row {
  align-items: center;
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: 10px;
  margin: 10px 0;
}

.month-bar {
  background: #eceff1;
  height: 8px;
}

.month-bar span {
  background: #00838f;
  display: block;
  height: 8px;
}

.period-row {
  border-bottom: 1px solid #eeeeee;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  padding: 10px 0;
}

.provider-row {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
}

.period-total-row {
  background: #f7fafb;
  border: 1px solid #e3ebee;
  border-radius: 4px;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr auto;
  margin-top: 12px;
  padding: 11px 12px;
}

.period-note {
  font-size: 0.86rem;
  margin: 8px 0 0;
}

.period-progress {
  grid-column: 1 / -1;
  margin: 0;
}

.encounter-filter-grid {
  align-items: end;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(260px, 1.4fr) minmax(170px, 0.8fr) minmax(170px, 0.8fr) auto;
  margin-top: 12px;
}

.encounter-filter-grid .input-field {
  margin: 0;
}

.encounter-search {
  margin-top: 0;
}

.encounter-actions {
  text-align: right;
}

.encounter-filter-grid + .collection {
  margin-top: 18px;
}

.encounter-pager {
  align-items: center;
  border-top: 1px solid #eeeeee;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 14px;
}

.encounter-pager span {
  color: #607d8b;
  font-size: 0.88rem;
  font-weight: 600;
}

.encounter-page-button {
  color: #455a64;
  font-weight: 600;
  text-transform: none;
}

.invoice-stack {
  background: #eceff1;
  display: flex;
  height: 8px;
  margin: 10px 0;
  width: 100%;
}

.period-stack {
  grid-column: 1 / -1;
  margin-bottom: 0;
  margin-top: 0;
}

.invoice-stack span {
  display: block;
  height: 8px;
}

.progress .determinate,
.month-bar span,
.invoice-stack span {
  animation: insightBarIn 0.38s ease both;
  transform-origin: left center;
  transition: width 0.28s ease, filter 0.18s ease;
}

.progress:hover .determinate,
.month-bar:hover span,
.invoice-stack:hover span {
  filter: saturate(1.15);
}

.invoice-stack .paid {
  background: #26a69a;
}

.invoice-stack .pending {
  background: #ffca28;
}

.legend-chip {
  display: inline-block;
  margin-right: 10px;
}

.legend-box {
  display: inline-block;
  height: 8px;
  margin-right: 5px;
  width: 16px;
}

.legend-box.paid {
  background: #26a69a;
}

.legend-box.pending {
  background: #ffca28;
}

@media only screen and (max-width: 992px) {
  .overview-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 600px) {
  .principal-overview-head {
    align-items: flex-start;
    flex-direction: column;
    padding: 22px;
  }

  .principal-overview-body {
    padding: 22px;
  }

  .overview-metrics,
  .month-row,
  .period-total-row,
  .encounter-filter-grid {
    grid-template-columns: 1fr;
  }

  .encounter-actions {
    text-align: left;
  }

  .encounter-pager {
    align-items: stretch;
    flex-direction: column;
  }
}

@keyframes insightCardIn {
  from {
    opacity: 0.88;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes insightBarIn {
  from {
    opacity: 0.55;
    transform: scaleX(0.35);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}
</style>
